"use client";

import type { LeverageRow } from "@/lib/types/financial-snapshot";
import { niceScale, CHART_PADDING, CHART_HEIGHT, YAxis, XAxisLabels, ChartSvg } from "../ChartAxes";
import { useChartHover } from "../hover/use-chart-hover";
import { HoverHitAreas } from "../hover/HoverHitAreas";
import { HoverCrosshair } from "../hover/HoverCrosshair";
import { ChartTooltip } from "../hover/ChartTooltip";
import type { HoverPoint } from "../hover/types";

const VIEW_WIDTH = 640;

const fmtRatio = (v: number) => `${v.toFixed(2)}x`;
const fmtDe = (v: number) => v.toFixed(2);

export function LeverageChart({ rows }: { rows: LeverageRow[] }) {
  const { containerRef, activeIndex, handlePointerMove, handlePointerLeave, handlePointerUp } = useChartHover();

  const usable = rows.filter((r) => r.debtToEquity != null || r.interestCoverage != null);
  if (usable.length === 0) return null;

  const deVals = usable.map((r) => r.debtToEquity).filter((v): v is number => v != null);
  const icVals = usable.map((r) => r.interestCoverage).filter((v): v is number => v != null);
  const leftScale = niceScale(Math.max(...deVals, 0.5), 0);
  const rightScale = icVals.length > 0 ? niceScale(Math.max(...icVals, 1), 0) : null;

  const plotTop = CHART_PADDING.top;
  const plotBottom = CHART_HEIGHT - CHART_PADDING.bottom;
  const plotHeight = plotBottom - plotTop;
  const yLeft = (v: number) => plotBottom - ((v - leftScale.min) / (leftScale.max - leftScale.min)) * plotHeight;
  const yRight = rightScale
    ? (v: number) => plotBottom - ((v - rightScale.min) / (rightScale.max - rightScale.min)) * plotHeight
    : null;

  const groupWidth = (VIEW_WIDTH - CHART_PADDING.left - CHART_PADDING.right) / usable.length;
  const barWidth = groupWidth * 0.5;
  const xScale = (i: number) => CHART_PADDING.left + i * groupWidth + groupWidth / 2;

  const anchors = usable.map((_, i) => xScale(i));

  const points: HoverPoint[] = usable.map((row) => ({
    period: row.period,
    series: [
      { label: "Debt / Equity", value: row.debtToEquity, format: fmtDe, colorClass: "bg-amber-400" },
      { label: "Interest Cover", value: row.interestCoverage, format: fmtRatio, colorClass: "bg-cgreen" },
    ],
  }));

  const linePoints = yRight
    ? usable
        .map((r, i) =>
          r.interestCoverage != null ? `${xScale(i)},${yRight(r.interestCoverage)}` : null,
        )
        .filter((p): p is string => p !== null)
        .join(" ")
    : "";

  return (
    <div ref={containerRef} className="relative">
      <ChartSvg viewBox={`0 0 ${VIEW_WIDTH} ${CHART_HEIGHT}`}>
        <YAxis ticks={leftScale.ticks} scale={yLeft} format={(v) => v.toFixed(1)} width={VIEW_WIDTH} />
        {usable.map((row, i) => {
          if (row.debtToEquity == null) return null;
          const cx = xScale(i);
          const y = yLeft(row.debtToEquity);
          return (
            <rect key={row.period} x={cx - barWidth / 2} y={y} width={barWidth} height={plotBottom - y} className="fill-amber-400" rx={2} />
          );
        })}
        {linePoints && (
          <polyline fill="none" className="stroke-cgreen" strokeWidth={2} points={linePoints} />
        )}
        <XAxisLabels labels={usable.map((r) => r.period)} xScale={xScale} y={CHART_HEIGHT - 8} />

        <g transform={`translate(${CHART_PADDING.left}, 4)`}>
          <rect width={8} height={8} className="fill-amber-400" />
          <text x={12} y={8} fontSize={10} fill="currentColor" fillOpacity={0.7}>D/E</text>
          <rect x={70} width={8} height={8} className="fill-cgreen" />
          <text x={82} y={8} fontSize={10} fill="currentColor" fillOpacity={0.7}>Interest Coverage</text>
        </g>

        {activeIndex != null && <HoverCrosshair x={anchors[activeIndex]} plotTop={plotTop} plotBottom={plotBottom} />}
        <HoverHitAreas
          anchors={anchors}
          viewBoxWidth={VIEW_WIDTH}
          plotTop={plotTop}
          plotBottom={plotBottom}
          onActivate={handlePointerMove}
          onLeave={handlePointerLeave}
          onTap={handlePointerUp}
        />
      </ChartSvg>
      {activeIndex != null && (
        <ChartTooltip point={points[activeIndex]} anchorPct={(anchors[activeIndex] / VIEW_WIDTH) * 100} />
      )}
    </div>
  );
}
